import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  Loader2, Users, Trophy, CalendarDays, MessageCircle, ChevronLeft, Send,
  UserPlus, Crown, Shield, Upload, Check, X, Plus, Trash2, Calendar, Pencil,
  AlertTriangle,
} from "lucide-react";

/**
 * EA FC Pro Clubs hub (V2)
 * Route: /v2/proclubs  (overview)
 *        /v2/proclubs/fixtures/:id (fixture detail with chat + submit)
 */
export default function ProClubsV2() {
  const { user } = useAuth();
  const { t } = useT();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("team");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/proclubs/me");
      setMe(data);
    } catch { setMe({ error: true }); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { const id = setInterval(refresh, 20000); return () => clearInterval(id); }, [refresh]);

  if (!me) return <CenterLoader />;
  if (me.error) return <div className="text-center text-sm text-[#71717A] py-20">Pro Clubs unavailable.</div>;

  return (
    <div className="space-y-5 pb-8" data-testid="v2-proclubs">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00FF87] flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> {t("pcl.kicker")} · {me.pool}
          </div>
          <h1 className="text-2xl font-extrabold text-white mt-0.5">{t("pcl.title")}</h1>
        </div>
        <Link to="/v2/explore" className="text-[11px] font-mono text-[#71717A] hover:text-white inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> {t("pcl.backToHub")}
        </Link>
      </header>

      {/* No team → create form + pending invites */}
      {!me.team ? (
        <NoTeamView me={me} refresh={refresh} t={t} />
      ) : (
        <>
          <nav className="flex gap-2 -mx-1 px-1 overflow-x-auto" role="tablist">
            {[
              ["team",      t("pcl.tab.team"),      Users],
              ["fixtures",  t("pcl.tab.fixtures"),  CalendarDays],
              ["standings", t("pcl.tab.standings"), Trophy],
            ].map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`v2-pc-tab-${k}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
                  tab === k ? "bg-[#00FF87] text-[#06120A] shadow-lg" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
          {tab === "team"      && <TeamTab me={me} refresh={refresh} t={t} />}
          {tab === "fixtures"  && <FixturesTab me={me} t={t} />}
          {tab === "standings" && <StandingsTab me={me} t={t} />}
        </>
      )}
    </div>
  );
}

/* ============================================================== */
function NoTeamView({ me, refresh, t }) {
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("⚽");
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error(t("pcl.create.tooShort"));
    setBusy(true);
    try {
      await api.post("/proclubs/teams", { name: name.trim(), crest_emoji: emoji });
      toast.success(t("pcl.create.success"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.create.failed"));
    } finally { setBusy(false); }
  };

  const respondInvite = async (id, action) => {
    try {
      await api.post(`/proclubs/invites/${id}/${action}`);
      toast.success(action === "accept" ? t("pcl.create.success") : t("pcl.invites.decline"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.team.actionFail"));
    }
  };

  return (
    <div className="space-y-4">
      {me.open_league && (
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4" data-testid="v2-pc-league-status">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{t("pcl.league.current")}</div>
          <div className="text-base font-extrabold text-white">{t("pcl.league.seasonOf", { n: me.open_league.season_no, pool: me.open_league.pool })}</div>
          <div className="text-[11px] text-white/70 mt-1">
            {t("pcl.league.progress", { registered: me.open_league.teams_registered })} ·
            <span className="text-[#FFD700] font-bold ml-1">{t("pcl.league.toKickOff", { n: me.open_league.teams_needed })}</span>
          </div>
        </div>
      )}

      {me.invites?.length > 0 && (
        <div className="space-y-2" data-testid="v2-pc-pending-invites">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.invites.kicker")}</div>
          {me.invites.map((inv) => (
            <div key={inv.id} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex items-center gap-3" data-testid={`v2-pc-invite-${inv.id}`}>
              <div className="text-3xl">{inv.team?.crest_emoji || "⚽"}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold truncate">{inv.team?.name}</div>
                <div className="text-[10px] text-[#71717A]">{t("pcl.invites.captain", { name: inv.team?.captain?.username })}</div>
              </div>
              <button type="button" onClick={() => respondInvite(inv.id, "accept")}
                className="px-3 py-1.5 rounded-full bg-[#00FF87] text-[#06120A] text-[10px] font-extrabold uppercase tracking-widest"
                data-testid={`v2-pc-invite-accept-${inv.id}`}>{t("pcl.invites.accept")}</button>
              <button type="button" onClick={() => respondInvite(inv.id, "decline")}
                className="px-3 py-1.5 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest"
                data-testid={`v2-pc-invite-decline-${inv.id}`}>{t("pcl.invites.decline")}</button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={create} className="rounded-3xl p-5 bg-gradient-to-br from-[#0a3a25] via-[#0a4a2f] to-[#022014] ring-2 ring-[#00FF87]/50 space-y-3" data-testid="v2-pc-create-form">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.create.kicker")}</div>
        <div className="grid grid-cols-[60px_1fr] gap-2">
          <input type="text" value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4}
            data-testid="v2-pc-create-emoji"
            style={{ fontSize: "26px" }}
            className="rounded-xl bg-white/5 ring-1 ring-white/10 px-2 py-2 text-center text-white outline-none focus:ring-[#00FF87]" />
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("pcl.create.namePh")} maxLength={40}
            data-testid="v2-pc-create-name"
            style={{ fontSize: "16px" }}
            className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-white outline-none focus:ring-[#00FF87]" />
        </div>
        <button type="submit" disabled={busy}
          data-testid="v2-pc-create-submit"
          className="w-full py-2.5 rounded-full bg-[#00FF87] text-[#06120A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("pcl.create.btn")}
        </button>
        <p className="text-[10px] text-white/60">{t("pcl.create.subtitle")}</p>
      </form>
    </div>
  );
}

/* ============================================================== */
function TeamTab({ me, refresh, t }) {
  const team = me.team;
  const isCaptain = me.is_captain;
  const isOfficer = isCaptain || me.is_deputy;
  const [inviteId, setInviteId] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteId.trim()) return;
    setInviteBusy(true);
    try {
      await api.post(`/proclubs/teams/${team.id}/invite`, { to_user_id: inviteId.trim() });
      toast.success(t("pcl.team.inviteOk"));
      setInviteId("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.team.inviteFail"));
    } finally { setInviteBusy(false); }
  };

  const memberAction = async (action, uid) => {
    try {
      await api.post(`/proclubs/teams/${team.id}/${action}`, { user_id: uid });
      toast.success(t("pcl.team.actionOk"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pcl.team.actionFail"));
    }
  };

  return (
    <div className="space-y-3" data-testid="v2-pc-team-tab">
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#0a3a25] via-[#082916] to-[#02100a] ring-1 ring-[#00FF87]/40">
        <div className="flex items-center gap-3">
          <div className="text-5xl">{team.crest_emoji}</div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-extrabold text-white truncate">{team.name}</h2>
            <div className="text-[10px] font-mono text-[#71717A]">{t("pcl.team.size", { size: team.size, pool: team.pool })}</div>
          </div>
        </div>
      </div>

      {/* Members */}
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
        <div className="px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{t("pcl.team.squad")}</div>
        <div className="divide-y divide-white/5">
          <Member t={t} member={team.captain} role="captain" isMe={team.captain?.id === me.user_id} />
          {team.deputies.map((m) => (
            <Member t={t} key={m.id} member={m} role="deputy" isMe={m.id === me.user_id}
              onKick={isCaptain && m.id !== me.user_id ? () => memberAction("kick", m.id) : null}
              onTransfer={isCaptain ? () => memberAction("transfer-captain", m.id) : null} />
          ))}
          {team.players.map((m) => (
            <Member t={t} key={m.id} member={m} role="player" isMe={m.id === me.user_id}
              canPromote={isCaptain}
              onPromote={() => memberAction("promote", m.id)}
              onKick={isOfficer ? () => memberAction("kick", m.id) : null}
              onTransfer={isCaptain ? () => memberAction("transfer-captain", m.id) : null} />
          ))}
        </div>
      </div>

      {/* Invite form (officers only, only while team has room) */}
      {isOfficer && team.size < 10 && (
        <form onSubmit={sendInvite} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 flex gap-2" data-testid="v2-pc-invite-form">
          <input type="text" value={inviteId} onChange={(e) => setInviteId(e.target.value)}
            placeholder={t("pcl.team.invitePh")} data-testid="v2-pc-invite-user-id"
            style={{ fontSize: "16px" }}
            className="flex-1 bg-white/5 ring-1 ring-white/10 rounded-full px-3 py-2 text-white outline-none focus:ring-[#00FF87]" />
          <button type="submit" disabled={inviteBusy} data-testid="v2-pc-invite-send"
            className="px-3 py-2 rounded-full bg-[#00FF87] text-[#06120A] text-[11px] font-extrabold uppercase tracking-widest disabled:opacity-50">
            {inviteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          </button>
        </form>
      )}
    </div>
  );
}

function Member({ t, member, role, isMe, canPromote, onPromote, onKick, onTransfer }) {
  if (!member) return null;
  const RoleIcon = role === "captain" ? Crown : role === "deputy" ? Shield : Users;
  const roleColor = role === "captain" ? "text-[#FFD700]" : role === "deputy" ? "text-[#60A5FA]" : "text-white/70";
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`v2-pc-member-${member.id}`}>
      <RoleIcon className={`w-4 h-4 flex-shrink-0 ${roleColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate">@{member.username}{isMe && ` ${t("pcl.role.you")}`}</div>
        <div className={`text-[9px] font-mono uppercase tracking-widest ${roleColor}`}>{t(`pcl.role.${role}`)}</div>
      </div>
      {canPromote && (
        <button type="button" onClick={onPromote} title="Promote to deputy"
          data-testid={`v2-pc-promote-${member.id}`}
          className="px-2 py-1 rounded-full bg-[#60A5FA]/20 text-[#60A5FA] text-[9px] font-bold uppercase tracking-widest">{t("pcl.role.promote")}</button>
      )}
      {onTransfer && (
        <button type="button" onClick={onTransfer} title="Transfer captaincy"
          data-testid={`v2-pc-transfer-${member.id}`}
          className="px-2 py-1 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-[9px] font-bold uppercase tracking-widest">{t("pcl.role.transfer")}</button>
      )}
      {onKick && (
        <button type="button" onClick={onKick} title="Kick from team"
          data-testid={`v2-pc-kick-${member.id}`}
          className="px-2 py-1 rounded-full bg-[#E11D48]/20 text-[#F87171] text-[9px] font-bold uppercase tracking-widest">{t("pcl.role.kick")}</button>
      )}
    </div>
  );
}

/* ============================================================== */
function FixturesTab({ me, t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/proclubs/fixtures");
        setRows(data.fixtures || []);
      } catch { setRows([]); }
    })();
  }, []);
  if (!rows) return <CenterLoader />;
  if (rows.length === 0) return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-sm text-white/70">
      {t("pcl.fx.empty")}
    </div>
  );
  // Group by matchday
  const byDay = rows.reduce((acc, f) => {
    acc[f.matchday] = acc[f.matchday] || [];
    acc[f.matchday].push(f);
    return acc;
  }, {});
  const myTeamId = me.team?.id;
  return (
    <div className="space-y-4" data-testid="v2-pc-fixtures">
      {Object.keys(byDay).sort((a, b) => +a - +b).map((md) => (
        <div key={md} className="space-y-2">
          <div className="px-1 text-[10px] font-mono uppercase tracking-widest text-[#00FF87]">{t("pcl.fx.matchday")} {md}</div>
          {byDay[md].map((f) => {
            const involves = f.home_team_id === myTeamId || f.away_team_id === myTeamId;
            const dt = new Date(f.scheduled_at);
            return (
              <Link to={`/v2/proclubs/fixtures/${f.id}`} key={f.id}
                data-testid={`v2-pc-fixture-${f.id}`}
                className={`block rounded-2xl p-3 ring-1 transition-shadow ${
                  involves ? "bg-[#FFD700]/10 ring-[#FFD700]/50 hover:ring-[#FFD700]" : "bg-white/5 ring-white/10 hover:ring-white/20"
                }`}>
                <div className="flex items-center justify-between text-[10px] font-mono text-[#71717A]">
                  <span>{dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                  <span>{dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-white">
                  <span className="text-base">{f.home_team?.crest_emoji}</span>
                  <span className="flex-1 truncate text-sm font-bold">{f.home_team?.name}</span>
                  <span className="text-sm font-extrabold tabular-nums">
                    {f.status === "confirmed" ? `${f.home_score} - ${f.away_score}` : "—"}
                  </span>
                  <span className="flex-1 truncate text-sm font-bold text-right">{f.away_team?.name}</span>
                  <span className="text-base">{f.away_team?.crest_emoji}</span>
                </div>
                <div className="mt-1 text-[9px] font-mono uppercase tracking-widest text-right">
                  <StatusPill status={f.status} t={t} />
                </div>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status, t }) {
  const map = {
    scheduled:        { c: "text-[#FFD700]", k: "pcl.status.scheduled" },
    awaiting_scores:  { c: "text-[#60A5FA]", k: "pcl.status.awaiting" },
    confirmed:        { c: "text-[#00FF87]", k: "pcl.status.confirmed" },
    disputed:         { c: "text-[#E11D48]", k: "pcl.status.disputed" },
  };
  const m = map[status] || { c: "text-white/60", k: null };
  return <span className={m.c}>{m.k ? t(m.k) : status}</span>;
}

/* ============================================================== */
function StandingsTab({ me, t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/proclubs/standings");
        setRows(data.standings || []);
      } catch { setRows([]); }
    })();
  }, []);
  if (!rows) return <CenterLoader />;
  if (rows.length === 0) return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 text-center text-sm text-white/70">
      {t("pcl.fx.empty")}
    </div>
  );
  const myTeamId = me.team?.id;
  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-white/10" data-testid="v2-pc-standings">
      <div className="grid grid-cols-12 gap-1 px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-[#71717A] bg-white/5">
        <div className="col-span-1">#</div>
        <div className="col-span-5">{t("pcl.tab.team")}</div>
        <div className="col-span-1 text-center">P</div>
        <div className="col-span-1 text-center">W</div>
        <div className="col-span-1 text-center">D</div>
        <div className="col-span-1 text-center">L</div>
        <div className="col-span-1 text-center">GD</div>
        <div className="col-span-1 text-right">Pts</div>
      </div>
      {rows.map((r) => {
        const mine = r.team.id === myTeamId;
        return (
          <div key={r.team.id}
            className={`grid grid-cols-12 gap-1 px-3 py-2 text-[12px] items-center border-t border-white/5 ${mine ? "bg-[#FFD700]/10" : ""}`}
            data-testid={`v2-pc-standings-row-${r.team.id}`}>
            <div className="col-span-1 font-mono text-white/60">{r.rank}</div>
            <div className="col-span-5 flex items-center gap-1.5 min-w-0">
              <span className="text-base">{r.team.crest_emoji}</span>
              <span className={`font-bold truncate ${mine ? "text-[#FFD700]" : "text-white"}`}>{r.team.name}</span>
            </div>
            <div className="col-span-1 text-center text-white/80">{r.P}</div>
            <div className="col-span-1 text-center text-[#00FF87]">{r.W}</div>
            <div className="col-span-1 text-center text-white/70">{r.D}</div>
            <div className="col-span-1 text-center text-[#F87171]">{r.L}</div>
            <div className="col-span-1 text-center text-white/70">{r.GD > 0 ? `+${r.GD}` : r.GD}</div>
            <div className="col-span-1 text-right font-extrabold text-white">{r.Pts}</div>
          </div>
        );
      })}
    </div>
  );
}

function CenterLoader() {
  return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#00FF87]" /></div>;
}
